import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, ChevronDown } from 'lucide-react';

export default function HeroSection() {
  const { t } = useLanguage();

  const scrollTo = (e, id) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 bg-[#0B1E2E]" />
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A4D75]/30 via-transparent to-transparent" />
      <div className="hero-glow top-[-100px] right-[-100px]" />
      <div className="hero-glow bottom-[-200px] left-[-150px]" style={{ opacity: 0.5 }} />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 w-full">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="animate-fade-up mb-8 inline-flex items-center gap-2 px-4 py-2 rounded border border-[#42D2D9]/30 bg-[#42D2D9]/5">
            <div className="w-2 h-2 rounded-full bg-[#42D2D9] pulse-dot" />
            <span className="text-sm uppercase tracking-widest text-[#42D2D9] font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('hero.badge')}
            </span>
          </div>

          {/* Main heading */}
          <h1 className="animate-fade-up delay-100 text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="text-white">{t('hero.title_line1')}</span>
            <br />
            <span className="gradient-text">{t('hero.title_highlight')}</span>
            <br />
            <span className="text-white">{t('hero.title_line2')}</span>
            <br />
            <span className="text-[#42D2D9]">{t('hero.title_accent')}</span>
          </h1>

          {/* Subtitle */}
          <p className="animate-fade-up delay-200 text-base md:text-lg text-slate-400 mb-12 tracking-wide">
            {t('hero.subtitle')}
          </p>

          {/* CTAs */}
          <div className="animate-fade-up delay-300 flex flex-col sm:flex-row gap-4 items-start">
            <a
              href="#booking"
              onClick={(e) => scrollTo(e, '#booking')}
              data-testid="hero-cta-booking"
              className="cta-primary inline-flex items-center gap-3"
            >
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-4 h-4 relative z-10" />
            </a>
            <a
              href="#credits"
              onClick={(e) => scrollTo(e, '#credits')}
              data-testid="hero-cta-discover"
              className="text-white underline decoration-[#42D2D9] decoration-2 underline-offset-8 hover:text-[#42D2D9] transition-colors text-sm tracking-wide py-4"
            >
              {t('hero.cta_secondary')}
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <ChevronDown className="w-6 h-6 text-[#42D2D9]/50" />
      </div>
    </section>
  );
}
